import { Component, OnInit } from '@angular/core';
import { OperationError } from '../../../../../models/server-result/operation-error';
import { TranslateService } from '../../../../../services/translate/translate.service';
import { ServerApiService } from '../../../../../server-api/server-api.service';
import { LogService } from '../../../../../services/log/log.service';
import { ToasterService } from '../../../../../shared-components/custom-toaster/toaster.service';
import { Router, ActivatedRoute } from '@angular/router';
import { OperationResult } from '../../../../../models/server-result/operation-result';
import { HttpErrorResponse } from '@angular/common/http';
import { SupplierRecordValidation } from '../../../../../models/upload/suppliers/supplier-record-validation-result';
import { SupplierUploadedRecordValidationStatus } from '../../../../../models/upload/suppliers/supplier-uploaded-record-validation-status';
import { PublicContract } from '../../../../../models/contracts/confirming/public-contract';
import { ConfirmingContract } from '../../../../../models/contracts/confirming/confirming-contract';
import { SepaSearchCriteria } from '../../../../../models/sepa/sepa-search-criteria';
import { Sepa } from '../../../../../models/sepa/sepa';
import { Step } from '../../../../../shared-components/custom-stepper/custom-stepper.component';
import { ContractAndRelatedConnectionHelperService } from '../../../../../services/contract-related-connection/contract-related-connection-helper.service';
import { User } from '../../../../../models/User/user';
import { AppContextService } from '../../../../../services/app-context.service';
import { RouteAuthorizationService } from '../../../../../services/authorization/route-authorization.service';
import { ContractTabService } from '../../../../../services/contract-tab.service';

@Component({
	selector: 'app-suppliers-upload-step4',
	templateUrl: './suppliers-upload-step4.component.html',
	styleUrls: ['../upload-suppliers.scss'],
	standalone: false,
})
export class SuppliersUploadStep4Component implements OnInit {
	public isPerformingRequest = false;
	public showSubmitResult = false;
	public processedRecordsValidationErrors: OperationError[] = [];
	public uploadId: string;
	public associatedCompanyId: string;
	public invalidSupplierRecords: SupplierRecordValidation[] = [];
	public validSupplierRecords: SupplierRecordValidation[] = [];
	public totalValidRecords: number = 0;
	public totalInvalidRecords: number = 0;
	public showColumnActiveAutomaticFundingRequest: boolean = false;
	public showValidationResults: boolean = false;
	public loggedUser: User | null = null;
	private returnContractId: string = '';

	currentStep: number = 3;
	steps: Step[] = [];

	constructor(
		private translateService: TranslateService,
		private serverApiService: ServerApiService,
		private logger: LogService,
		private toasterService: ToasterService,
		private router: Router,
		private route: ActivatedRoute,
		private contractAndRelatedConnectionHelperService: ContractAndRelatedConnectionHelperService,
		private appContext: AppContextService,
		private routeAuthorizationService: RouteAuthorizationService,
		private contractTabService: ContractTabService
	) {
		this.initializeSteps();
	}

	initializeSteps(): void {
		this.steps = [
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step1Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step2Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step3Label', 'client'),
				icon: 'fa-solid fa-check',
			},
			{
				label: this.translateService.translate('supplier-upload-wizard-steps.Step4Label', 'client'),
				icon: 'fa-solid fa-check',
			},
		];
	}

	ngOnInit() {
		const queryParams = this.route.snapshot.queryParams;
		if (queryParams['returnContractId']) {
			this.returnContractId = queryParams['returnContractId'];
		}

		this.appContext.getLoggedUserAsync().subscribe((userResult) => {
			this.loggedUser = userResult.Data;
			if (
				(this.loggedUser?.IsBuyer && this.loggedUser?.IsCompanyAdmin) ||
				(this.loggedUser?.IsSystemAdmin && !this.loggedUser.IsApiIntegrationUser)
			) {
				this.route.params.subscribe((params) => {
					this.uploadId = params['uploadId'];
					this.associatedCompanyId = params['associatedCompanyId'];

					if (this.uploadId && this.associatedCompanyId) {
						const serviceCall = this.serverApiService.getSupplierRecordsByUploadId(this.uploadId);
						this.isPerformingRequest = true;
						serviceCall.subscribe({
							next: (operationResult: OperationResult<SupplierRecordValidation[]>) => {
								this.isPerformingRequest = false;
								if (operationResult.IsValid) {
									this.processValidGetRecordsByUploadIdResult(operationResult);
								} else {
									this.processInvalidGetRecordsByUploadIdResult(operationResult);
								}
							},
							error: (httpErrorResponse: HttpErrorResponse) => {
								this.isPerformingRequest = false;
								this.logger.error('[SuppliersUploadStep4Component::getValidRecordsByUploadId] HttpErrorResponse: ', httpErrorResponse);
								this.toasterService.showError(
									'[SuppliersUploadStep4Component::getValidRecordsByUploadId] HttpErrorResponse: ' +
										httpErrorResponse.error +
										httpErrorResponse.message
								);
							},
						});
					} else {
						this.toasterService.showError(this.translateService.translate('COULD_NOT_GET_UPLOAD_ID_FROM_ROUTE', 'list-errorCodes'));
					}
				});
			} else {
				this.routeAuthorizationService.routeOnUnauthorized();
			}
		});
	}

	processInvalidGetRecordsByUploadIdResult(operationResult: OperationResult<SupplierRecordValidation[]>): void {
		const errors = operationResult.Errors;

		if (errors && errors.length > 0) {
			this.toasterService.showError(this.translateService.translate('VALIDATION_FAILED', 'list-errorCodes'), {
				errors: errors,
				autoClose: false,
			});
		}
	}

	processValidGetRecordsByUploadIdResult(operationResult: OperationResult<SupplierRecordValidation[]>): void {
		this.showValidationResults = true;
		this.invalidSupplierRecords = operationResult.Data.filter((r) => r.ValidationStatus == SupplierUploadedRecordValidationStatus.Invalid);
		this.validSupplierRecords = operationResult.Data.filter((r) => r.ValidationStatus == SupplierUploadedRecordValidationStatus.Valid);

		this.totalInvalidRecords = this.invalidSupplierRecords.length;
		this.totalValidRecords = this.validSupplierRecords.length;

		const contractsIds: string[] = [];

		this.invalidSupplierRecords.forEach((contentData: SupplierRecordValidation) => {
			if (!!contentData.ConfirmingContractId && !contractsIds.find((c) => c == contentData.ConfirmingContractId))
				contractsIds.push(contentData.ConfirmingContractId);
		});

		if (!!contractsIds && contractsIds.length > 0) {
			const getContractsByIdsServiceCall = this.serverApiService.getConfirmingContractsByIds(contractsIds);

			getContractsByIdsServiceCall.subscribe({
				next: (operationResult: OperationResult<ConfirmingContract[]>) => {
					if (operationResult.IsValid) {
						// get contracts for each payment request
						this.invalidSupplierRecords.forEach((contentData: SupplierRecordValidation) => {
							contentData.ConfirmingContract = operationResult.Data.find((c) => c.Id === contentData.ConfirmingContractId) || null;
						});

						this.isAutomaticFundingRequestColumnVisible(this.invalidSupplierRecords);
					} else {
						console.warn('Error retrieving contract information using getConfirmingContractsByIds... IsValid=false');
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				},
			});
		}

		if (this.invalidSupplierRecords?.some((contentData: SupplierRecordValidation) => !!contentData.BankCountryCode)) {
			const sepaSearchCriteria: SepaSearchCriteria = new SepaSearchCriteria();
			const getSepaConfigurations = this.serverApiService.getSepaConfigurations(sepaSearchCriteria);

			getSepaConfigurations.subscribe({
				next: (operationResult: OperationResult<Sepa[]>) => {
					if (operationResult.IsValid) {
						// PR: Get the country name for each bank country code.
						this.invalidSupplierRecords.forEach((contentData: SupplierRecordValidation) => {
							if (!!contentData.BankCountryCode) {
								contentData.BankCountry =
									operationResult.Data.find((c) => c.CountryCode === contentData.BankCountryCode)?.CountryName || '';
							}
						});
					} else {
						console.warn('Error retrieving SEPA configuration information using getSepaConfigurations... IsValid=false');
					}
				},
				error: (httpErrorResponse: HttpErrorResponse) => {
					console.error('error: ' + httpErrorResponse.message);
					this.toasterService.showErrorFromHttpResponse(httpErrorResponse);
				},
			});
		}
	}

	public backToValidationStep(): void {
		const queryParams: any = {};
		if (this.returnContractId) {
			queryParams.returnContractId = this.returnContractId;
		}

		const currentQueryParams = this.route.snapshot.queryParams;
		if (currentQueryParams['buyerId']) {
			queryParams.buyerId = currentQueryParams['buyerId'];
		}

		this.router.navigate(['/private/files-upload/upload/suppliers/step3', this.associatedCompanyId, this.uploadId], {
			queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
		});
	}

	public goToInvitationsSearch(): void {
		if (this.returnContractId) {
			this.contractTabService.setSelectedTab('confirming', 'suppliers');
			this.router.navigate(['/private/contracts/confirming/details', this.returnContractId]);
		} else {
			this.router.navigate(['/private/invitation/company/search']);
		}
	}

	private isAutomaticFundingRequestColumnVisible(contentValidationData: SupplierRecordValidation[]): void {
		this.showColumnActiveAutomaticFundingRequest = contentValidationData?.some((t) =>
			this.contractAndRelatedConnectionHelperService.canActivateAutomaticFundindRequest(t.ConfirmingContract as PublicContract)
		);
	}
}
